export const SYSTEM_EVENT_SEVERITIES = ['INFO', 'WARNING', 'ERROR', 'CRITICAL'];
export const SYSTEM_EVENT_SOURCES = [
  'API',
  'AUTH',
  'BACKUP',
  'RESTORE',
  'EMAIL',
  'PAYMENT',
  'TRADING',
  'DOCUMENTS',
  'KYC',
  'QUEUE',
  'MERCURE',
  'SYSTEM',
];
export const SYSTEM_EVENT_REGULATORY_CATEGORIES = [
  'OPERATIONAL',
  'SECURITY',
  'CLIENT_ORDER',
  'TRANSACTION',
  'COMMUNICATION',
  'KYC',
  'APPROPRIATENESS',
  'COMPLAINT',
  'PAYMENT',
  'ADMIN_ACTION',
  'DATA_ACCESS',
  'SYSTEM_CHANGE',
];
export const SYSTEM_EVENT_TYPES = [
  'auth.login_failed',
  'auth.password_reset_failed',
  'auth.rate_limited',
  'auth.two_factor_admin_reset',
  'auth.two_factor_disabled',
  'auth.two_factor_enabled',
  'auth.two_factor_failed',
  'auth.two_factor_recovery_codes_regenerated',
  'backup.tenant_db_failed',
  'backup.tenant_object_failed',
  'documents.workflow_failed',
  'email.delivery_failed',
  'email.system_sender_problem',
  'kyc.workflow_failed',
  'payment.deposit_failed',
  'payment.withdrawal_failed',
  'queue.scheduled_task_failed',
  'restore.failed',
  'restore.requested',
  'security.permission_changed',
  'system.compliance_mode_changed',
  'system.regulatory_admin_action',
  'trading.account_sync_failed',
  'trading.closed_trades_sync_failed',
];

const SUMMARY_CARD_CONFIG = [
  { key: 'total24h', label: 'systemEvents.summaryTotal24h', iconClass: 'ti ti-activity', toneClass: 'text-bg-primary' },
  { key: 'critical24h', label: 'systemEvents.summaryCritical24h', iconClass: 'ti ti-alert-octagon', toneClass: 'text-bg-danger' },
  { key: 'errors24h', label: 'systemEvents.summaryErrors24h', iconClass: 'ti ti-alert-triangle', toneClass: 'text-bg-danger' },
  { key: 'warnings24h', label: 'systemEvents.summaryWarnings24h', iconClass: 'ti ti-alert-circle', toneClass: 'text-bg-warning' },
  { key: 'total7d', label: 'systemEvents.summaryTotal7d', iconClass: 'ti ti-calendar-stats', toneClass: 'text-bg-secondary' },
];

const SEVERITY_BADGE_CLASS = {
  INFO: 'bg-info-subtle text-info',
  WARNING: 'bg-warning-subtle text-warning',
  ERROR: 'bg-danger-subtle text-danger',
  CRITICAL: 'bg-danger text-white',
};

const SYSTEM_EVENT_TYPE_LABELS = {
  'backup.tenant_db_failed': 'Backup database failed',
  'backup.tenant_object_failed': 'Backup object storage failed',
};

function removeTenantTerminology(value) {
  return String(value || '')
    .replace(/\btenant[-_\s]+safe\b/gi, 'sanitized')
    .replace(/\btenant[-_\s]+scoped\b/gi, 'system')
    .replace(/\btenant[-_\s]+level\b/gi, 'system')
    .replace(/\btenant\s+restore\b/gi, 'restore')
    .replace(/\bcurrent\s+tenant\b/gi, 'this system')
    .replace(/\btenant\s+database\b/gi, 'database')
    .replace(/\btenant\b/gi, 'system')
    .trim();
}

function formatNumber(value) {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('en-US').format(Number.isFinite(numeric) ? numeric : 0);
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(date);
}

function humanizeEnum(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  return normalized
    .toLowerCase()
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function formatSystemEventTypeLabel(value) {
  const normalized = String(value || '').trim();
  return SYSTEM_EVENT_TYPE_LABELS[normalized] || removeTenantTerminology(humanizeEnum(normalized));
}

function formatReference(reference) {
  if (!reference) {
    return '-';
  }

  if (reference.label) {
    return reference.label;
  }

  const type = String(reference.type || '').trim();
  const id = String(reference.id || '').trim();

  return [type, id].filter(Boolean).join(': ') || '-';
}

function formatCorrelation(event) {
  const values = [event?.requestId, event?.correlationId].filter(Boolean);
  return values.length > 0 ? values.join(' / ') : '-';
}

function normalizeDateTimeLocalAsUtc(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  if (/Z$|[+-]\d{2}:?\d{2}$/.test(raw)) {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? raw : date.toISOString();
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toISOString();
}

export function getDefaultSystemEventFilters() {
  return {
    from: '',
    to: '',
    severity: '',
    source: '',
    regulatoryCategory: '',
    eventType: '',
    requestId: '',
    correlationId: '',
    actorType: '',
    actorId: '',
    subjectType: '',
    subjectId: '',
  };
}

export function buildSystemEventQuery(filters = {}, options = {}) {
  const query = {
    limit: Number.isFinite(Number(options.limit)) ? Number(options.limit) : 50,
  };

  if (options.cursor) {
    query.cursor = String(options.cursor);
  }

  const supportedFilters = [
    'severity',
    'source',
    'regulatoryCategory',
    'eventType',
    'requestId',
    'correlationId',
    'actorType',
    'actorId',
    'subjectType',
    'subjectId',
  ];

  const from = normalizeDateTimeLocalAsUtc(filters.from);
  const to = normalizeDateTimeLocalAsUtc(filters.to);
  if (from) query.from = from;
  if (to) query.to = to;

  supportedFilters.forEach((key) => {
    const value = String(filters[key] || '').trim();
    if (value) {
      query[key] = value;
    }
  });

  return query;
}

export function buildSystemEventSummaryCards(summary) {
  const values = {
    total24h: summary?.last24h?.total,
    critical24h: summary?.last24h?.severity?.CRITICAL,
    errors24h: summary?.last24h?.severity?.ERROR,
    warnings24h: summary?.last24h?.severity?.WARNING,
    total7d: summary?.last7d?.total,
  };

  return SUMMARY_CARD_CONFIG.map((config) => ({
    ...config,
    value: formatNumber(values[config.key]),
  }));
}

export function buildEventTableRows(events) {
  return (Array.isArray(events) ? events : []).map((event) => ({
    ...event,
    key: event.id,
    occurredAtLabel: formatDateTime(event.occurredAt),
    recordedAtLabel: formatDateTime(event.recordedAt),
    severityLabel: humanizeEnum(event.severity),
    severityBadgeClass: SEVERITY_BADGE_CLASS[event.severity] || 'bg-secondary-subtle text-secondary',
    sourceLabel: humanizeEnum(event.source),
    categoryLabel: humanizeEnum(event.regulatoryCategory),
    retentionLabel: humanizeEnum(event.retentionClass),
    eventTypeLabel: formatSystemEventTypeLabel(event.eventType),
    title: removeTenantTerminology(event.title || event.eventType || event.id),
    actorLabel: formatReference(event.actor),
    subjectLabel: formatReference(event.subject),
    correlationLabel: formatCorrelation(event),
    archiveLabel: event.archived ? 'Archived' : 'Hot',
    archiveBadgeClass: event.archived ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary',
  }));
}

export function buildEventDetailModel(event) {
  if (!event) {
    return null;
  }

  const [row] = buildEventTableRows([event]);

  return {
    ...row,
    contextJson: row.context ? JSON.stringify(row.context, null, 2) : '{}',
    archiveManifestHash: row.archiveManifestHash || '-',
  };
}
